import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/site/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => { if (user && user !== false) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) { toast.success("Welcome back"); navigate("/admin"); }
    else toast.error(res.error);
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); toast.success("If an account exists, a reset email has been prepared."); setForgot(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1721428360480-0d4121f629c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Homio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#111827]/60" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <Logo light className="text-3xl" />
          <p className="mt-4 text-white/80 max-w-sm">Manage properties, enquiries and referrals from your Homio admin dashboard.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo className="text-2xl" /></div>
          <h1 className="font-display text-3xl font-bold text-[#111827]">{forgot ? "Reset password" : "Admin sign in"}</h1>
          <p className="mt-2 text-muted-foreground text-sm">{forgot ? "Enter your email to receive a reset link." : "Sign in to manage your platform."}</p>

          <form onSubmit={forgot ? sendReset : submit} className="mt-8 space-y-4">
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" /></div>
            {!forgot && <div className="space-y-1.5"><Label>Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" /></div>}
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#C81D35] hover:bg-[#a5162b]" data-testid="login-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : forgot ? "Send reset link" : "Sign in"}
            </Button>
            <button type="button" onClick={() => setForgot(!forgot)} className="text-sm text-[#C81D35] font-semibold w-full text-center" data-testid="forgot-toggle">
              {forgot ? "Back to sign in" : "Forgot your password?"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
